import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { PlusJakartaSansFontBase64String } from "../../Helpers/PlusJakartaSans-base64String";
import { imgData } from "../../Helpers/smestLogoBase64ImgData";
import { generateLiquidityIndicatorSection } from "./generateLiquidityIndicatorSection";
import type { ILiquidityIndicator } from "./liquidityIndicator";

declare module "jspdf" {
  interface jsPDF {
    getImageProperties?: (imageData: string | HTMLImageElement) => {
      width: number;
      height: number;
    };
  }
}

// ---------- Helpers ----------
const setFontIfAny = (doc: jsPDF) => {
  try {
    if (PlusJakartaSansFontBase64String) {
      doc.addFileToVFS(
        "PlusJakartaSans-VariableFont_wght-normal.ttf",
        PlusJakartaSansFontBase64String
      );
      doc.addFont(
        "PlusJakartaSans-VariableFont_wght-normal.ttf",
        "PlusJakartaSans",
        "normal"
      );
      doc.addFont(
        "PlusJakartaSans-VariableFont_wght-normal.ttf",
        "PlusJakartaSans",
        "bold"
      );
      doc.setFont("PlusJakartaSans", "normal");
    }
  } catch {}
};

const trySetFont = (
  doc: jsPDF,
  name: string,
  style: any,
  fallback: { name: string; style: any } = {
    name: "helvetica",
    style: "normal",
  }
) => {
  try {
    doc.setFont(name, style as any);
  } catch {
    try {
      doc.setFont(fallback.name, fallback.style as any);
    } catch {}
  }
};

const drawCompanyLogoTopRight = (doc: jsPDF, M: number) => {
  const W = doc.internal.pageSize.getWidth();
  try {
    if (imgData) {
      const dataUrl = imgData.startsWith("data:")
        ? imgData
        : `data:image/png;base64,${imgData}`;
      const maxW = 140,
        maxH = 48;
      let drawW = 120,
        drawH = 40;
      if (typeof (doc as any).getImageProperties === "function") {
        const p = (doc as any).getImageProperties!(dataUrl);
        const scale = Math.min(
          maxW / (p.width || maxW),
          maxH / (p.height || maxH)
        );
        drawW = (p.width || maxW) * scale;
        drawH = (p.height || maxH) * scale;
      }
      doc.addImage(dataUrl, "PNG", W - M - drawW, M - 6, drawW, drawH);
    }
  } catch {}
};

const ensureSpace = (
  doc: jsPDF,
  y: number,
  needed: number,
  M: number,
  headerHeight: number
) => {
  const H = doc.internal.pageSize.getHeight();
  if (y + needed <= H - M) return y;
  doc.addPage("a4");
  drawCompanyLogoTopRight(doc, M);
  return M + headerHeight + 8;
};

const CARD_BORDER = 220;
const CARD_FILL = { r: 236, g: 243, b: 255 };
const PAD_L = 16;
const PAD_R = 22;
const PAD_TOP = 22;
const PAD_BOTTOM = 10;
const GAP_LABEL_CONTENT = 10;
const LABEL_SIZE = 11;
const TEXT_SIZE = 10.25;
const LABEL_WEIGHT: "bold" = "bold";

const drawCard = (doc: jsPDF, x: number, y: number, w: number, h: number) => {
  doc.setLineWidth(0.6);
  doc.setDrawColor(CARD_BORDER);
  doc.setFillColor(CARD_FILL.r, CARD_FILL.g, CARD_FILL.b);
  doc.roundedRect(x, y, w, h, 10, 10, "FD");
};

const niceTicks = (minVal: number, maxVal: number, maxTicks = 5) => {
  if (!isFinite(minVal) || !isFinite(maxVal))
    return { min: 0, max: 1, step: 0.2, ticks: [0, 0.2, 0.4, 0.6, 0.8, 1] };
  if (minVal === maxVal) {
    const pad = Math.abs(minVal || 1) * 0.5;
    minVal -= pad;
    maxVal += pad;
  }
  const range = maxVal - minVal;
  const rawStep = range / (maxTicks || 5);
  const mag = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep))));
  const norm = rawStep / mag;
  let step = mag;
  if (norm < 1.5) step = 1 * mag;
  else if (norm < 3) step = 2 * mag;
  else if (norm < 7) step = 5 * mag;
  else step = 10 * mag;
  const niceMin = Math.floor(minVal / step) * step;
  const niceMax = Math.ceil(maxVal / step) * step;
  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + 1e-9; v += step)
    ticks.push(Number(v.toFixed(6)));
  return { min: niceMin, max: niceMax, step, ticks };
};

const sanitize = (t: any) => {
  try {
    if (t == null) return "";
    const s = String(t)
      .replace(/&nbsp;/g, " ")
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return s;
  } catch {
    return "";
  }
};

const normalizeRupees = (t: any) => {
  // Convert INR/Rs to ₹ and normalize spacing
  const s = sanitize(t);
  try {
    return s
      .replace(/(?:&#8377;|₹)/g, "₹")
      .replace(/\b(?:Rs\.?|INR)\b\s*\/?-?\s*/gi, "₹ ")
      .replace(/₹\s*(\d)/g, "₹ $1")
      .replace(/\s{2,}/g, " ")
      .trim();
  } catch {
    return s;
  }
};

const toTitleCase = (s: string) => {
  try {
    return String(s || "")
      .toLowerCase()
      .replace(
        /\b([a-z])(\w*)/g,
        (_: any, a: string, b: string) => a.toUpperCase() + b
      );
  } catch {
    return String(s || "");
  }
};

// Pie helpers
const hexToRgb = (hex: string) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  };
};
const mix = (c: { r: number; g: number; b: number }, whiteFactor: number) => ({
  r: Math.round(c.r + (255 - c.r) * whiteFactor),
  g: Math.round(c.g + (255 - c.g) * whiteFactor),
  b: Math.round(c.b + (255 - c.b) * whiteFactor),
});
// Robust wedge via triangles
const drawWedge = (
  doc: jsPDF,
  cx: number,
  cy: number,
  r: number,
  a0: number,
  a1: number,
  fill: { r: number; g: number; b: number }
) => {
  const step = Math.max((2 * Math.PI) / 180, (a1 - a0) / 60); // ~2° steps
  doc.setFillColor(fill.r, fill.g, fill.b);
  for (let t = a0; t < a1; t += step) {
    const t2 = Math.min(a1, t + step);
    const x1 = cx + r * Math.cos(t);
    const y1 = cy + r * Math.sin(t);
    const x2 = cx + r * Math.cos(t2);
    const y2 = cy + r * Math.sin(t2);
    doc.triangle(cx, cy, x1, y1, x2, y2, "F");
  }
};

type Options = { startY?: number; liquidityIndicator?: ILiquidityIndicator };

// ---------- Main ----------
export async function generateBondQuotationFourthPage(
  doc: jsPDF,
  bond: any,
  calculated: any,
  userDetails: any,
  report: any,
  news: any[],
  opts: Options = {}
): Promise<{ doc: jsPDF; nextY: number }> {
  setFontIfAny(doc);

  const M = (doc as any).__margin ?? 36;
  const headerHeight = (doc as any).__headerHeight ?? 42;
  const W = doc.internal.pageSize.getWidth();

  let y =
    typeof opts.startY === "number"
      ? opts.startY
      : (doc as any).__nextY ?? M + headerHeight + 12;
  if (y < M + headerHeight + 8) y = M + headerHeight + 8;

  const fullW = W - 2 * M;

  // ===== 1) Revenue/Profit line chart =====
  const chartHeading = report?.quarterlyRevenueHeading ?? "yearly revenue";
  const chartRows = (report?.quarterlyRevenues ?? []).map((r: any) => ({
    halfYear: (r?.halfYear ?? "").toString(),
    revenue: Number(r?.revenue ?? 0),
    profit: Number(r?.profitAfterTax ?? 0),
  }));
  const chartHasSeries = chartRows.length >= 2;

  const chartLabelH = 14;
  const chartLegendH = 20;
  const chartLeftAxisW = 40;
  const chartRightPad = 8;
  const chartBottomAxisH = 26;
  const chartInnerH = 220;

  const chartCardH =
    PAD_TOP +
    chartLabelH +
    GAP_LABEL_CONTENT +
    chartLegendH +
    chartInnerH +
    chartBottomAxisH +
    PAD_BOTTOM;
  y = ensureSpace(doc, y, chartCardH, M, headerHeight);
  doc.setLineWidth(0.6);
  doc.setDrawColor(225);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(M, y, fullW, chartCardH, 10, 10, "FD");

  try {
    doc.setFont("helvetica", LABEL_WEIGHT);
  } catch {
    doc.setFont(undefined as any, LABEL_WEIGHT);
  }
  doc.setFontSize(LABEL_SIZE);
  doc.setTextColor(40);
  doc.text(chartHeading, M + PAD_L, y + PAD_TOP);

  // Legend (top-right)
  const swW = 14,
    swH = 4,
    gap = 8;
  const chartLegendY = y + PAD_TOP + chartLabelH + 2;
  const chartLegendXRight = M + fullW - PAD_R;
  const chartLabelRev = "Revenue (cr)";
  const chartLabelProf = "Profit (cr)";
  doc.setFontSize(9);
  doc.setTextColor(42);
  const chartRevW = doc.getTextWidth(chartLabelRev);
  const chartProfW = doc.getTextWidth(chartLabelProf);
  const chartLegendTotalW = swW + 4 + chartRevW + gap + swW + 4 + chartProfW;
  let chartLX = chartLegendXRight - chartLegendTotalW;
  // Revenue
  doc.setDrawColor(117, 105, 238);
  doc.setFillColor(117, 105, 238);
  doc.roundedRect(chartLX, chartLegendY - swH + 2, swW, swH, 2, 2, "FD");
  chartLX += swW + 4;
  doc.text(chartLabelRev, chartLX, chartLegendY);
  chartLX += chartRevW + gap;
  // Profit
  doc.setDrawColor(81, 203, 97);
  doc.setFillColor(81, 203, 97);
  doc.roundedRect(chartLX, chartLegendY - swH + 2, swW, swH, 2, 2, "FD");
  chartLX += swW + 4;
  doc.text(chartLabelProf, chartLX, chartLegendY);

  // Chart frame
  const chartX = M + PAD_L + chartLeftAxisW;
  const chartY = y + PAD_TOP + chartLabelH + GAP_LABEL_CONTENT + chartLegendH;
  const chartW = fullW - PAD_L - PAD_R - chartLeftAxisW - chartRightPad;

  const allVals = chartRows
    .flatMap((r: any) => [r.revenue, r.profit])
    .filter((n: any) => isFinite(n));
  const minVal = allVals.length ? Math.min(...allVals) : 0;
  const maxVal = allVals.length ? Math.max(...allVals) : 1;
  const { min: yMin, max: yMax, ticks } = niceTicks(minVal, maxVal, 5);
  const yRange = yMax - yMin || 1;

  doc.setFontSize(8);
  doc.setTextColor(70);
  doc.setDrawColor(223, 224, 228);
  ticks.forEach((tv) => {
    const ty = chartY + chartInnerH - ((tv - yMin) / yRange) * chartInnerH;
    doc.line(chartX, ty, chartX + chartW, ty);
    const label = String(tv);
    const tw = doc.getTextWidth(label);
    doc.text(label, chartX - 6 - tw, ty + 2);
  });
  doc.setDrawColor(120);
  doc.line(chartX, chartY, chartX, chartY + chartInnerH);
  doc.line(chartX, chartY + chartInnerH, chartX + chartW, chartY + chartInnerH);

  // X labels
  const nPts = chartRows.length;
  const xStep = chartW / Math.max(1, nPts - 1);
  const labelStep = nPts > 10 ? Math.ceil(nPts / 8) : 1;
  doc.setFontSize(8);
  doc.setTextColor(60);
  chartRows.forEach((r: any, i: number) => {
    const cx = chartX + i * xStep;
    if (i % labelStep === 0) {
      const text = r.halfYear;
      const tw = doc.getTextWidth(text);
      let tx = cx - tw / 2;
      if (tx < chartX) tx = chartX;
      if (tx + tw > chartX + chartW) tx = chartX + chartW - tw;
      doc.text(text, tx, chartY + chartInnerH + 12);
    }
  });

  if (chartHasSeries) {
    // Revenue
    doc.setDrawColor(117, 105, 238);
    doc.setLineWidth(1.4);
    for (let i = 1; i < nPts; i++) {
      const x1 = chartX + (i - 1) * xStep,
        y1 =
          chartY +
          chartInnerH -
          ((chartRows[i - 1].revenue - yMin) / yRange) * chartInnerH;
      const x2 = chartX + i * xStep,
        y2 =
          chartY +
          chartInnerH -
          ((chartRows[i].revenue - yMin) / yRange) * chartInnerH;
      doc.line(x1, y1, x2, y2);
    }
    // Profit
    doc.setDrawColor(81, 203, 97);
    doc.setLineWidth(1.4);
    for (let i = 1; i < nPts; i++) {
      const x1 = chartX + (i - 1) * xStep,
        y1 =
          chartY +
          chartInnerH -
          ((chartRows[i - 1].profit - yMin) / yRange) * chartInnerH;
      const x2 = chartX + i * xStep,
        y2 =
          chartY +
          chartInnerH -
          ((chartRows[i].profit - yMin) / yRange) * chartInnerH;
      doc.line(x1, y1, x2, y2);
    }
  } else {
    doc.setFontSize(10);
    doc.setTextColor(90);
    doc.text(
      "Not enough data to plot revenue/profit trends.",
      chartX,
      chartY + chartInnerH / 2
    );
  }

  y = y + chartCardH + 12;

  // ===== 3) Shareholding pattern row (equal-size cards) =====
  const shRowGapX = 12;
  const shCardOut = 40,
    shCardIn = 24; // donut radii
  const shCardTitleH = 12;
  const shLegendFont = 7.6; // smaller legend font
  const shLegendLineH = 9.8; // line height
  const shLegendGap = 3; // gap between legend lines
  const legendCorner = 8;
  const legendInnerPadX = 8;
  const legendInnerPadY = 12;
  const spaceBelowChart = 18; // space between donut and legend container

  const shpHeading =
    report?.shareholdingPatternHeading ?? "Shareholding pattern";
  const shpRaw = (report?.shareholdingPatterns ?? []) as Array<{
    tag: string;
    value: string | number;
  }>;
  const catRaw = (report?.categoryWiseSHPRecords ?? []) as Array<{
    id: number;
    categoryName: string;
    particular: string;
    shareholding: string;
  }>;

  // Main items (title-cased)
  const shpAll = (shpRaw || [])
    .map((d) => ({
      tag: toTitleCase(String(d?.tag ?? "")),
      valueNum: Number(String(d?.value ?? "0").replace("%", "")),
    }))
    .filter((d) => isFinite(d.valueNum) && d.valueNum >= 0);
  shpAll.sort((a, b) => (a.tag === "Public" ? 1 : b.tag === "Public" ? -1 : 0));

  const baseOrange = "#FFB877";
  const baseBlue = "#4A97FF";
  const baseA = hexToRgb(baseOrange);
  const baseB = hexToRgb(baseBlue);

  // Color inheritance base map from main tags
  const baseMap: Record<string, { r: number; g: number; b: number }> = {};
  shpAll.forEach((d, idx) => {
    baseMap[d.tag] = idx % 2 === 0 ? baseA : baseB;
  });

  // Build cards: MAIN + sub-categories present
  type PieCard = {
    title: string;
    items: { tag: string; valueNum: number }[];
    parentBase?: { r: number; g: number; b: number };
  };
  const cards: PieCard[] = [];
  cards.push({ title: shpHeading, items: shpAll, parentBase: undefined });

  const grouped: Record<string, { tag: string; valueNum: number }[]> = {};
  (catRaw || []).forEach((rec) => {
    const cat = toTitleCase(String(rec?.categoryName ?? "").trim());
    const part = toTitleCase(String(rec?.particular ?? "").trim());
    const val = Number(String(rec?.shareholding ?? "0").replace("%", ""));
    if (!isFinite(val)) return;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ tag: part, valueNum: val });
  });
  ["Promoter", "Non-Promoter", "Public"].forEach((k) => {
    const g = grouped[k];
    if (g && g.length)
      cards.push({
        title: k,
        items: g,
        parentBase: baseMap[k] || (k === "Public" ? baseB : baseA),
      });
  });

  if (cards.length) {
    const shCols = cards.length;
    const rowWidth = fullW - (shCols - 1) * shRowGapX;
    const shW = rowWidth / shCols;

    // Measure each card (single-column legend)
    const measureCardH = (card: PieCard) => {
      const items = card.items;
      const sum = items.reduce((s, d) => s + d.valueNum, 0) || 1;
      const wrapW = shW - PAD_L - PAD_R - legendInnerPadX * 2;
      doc.setFontSize(shLegendFont);
      let linesCount = 0;
      items.forEach((d) => {
        const pct = `${((d.valueNum / sum) * 100).toFixed(1)}%`;
        const lbl = `${toTitleCase(d.tag)} ${pct}`;
        const wrapped = doc.splitTextToSize(lbl, wrapW);
        linesCount += Array.isArray(wrapped) ? wrapped.length : 1;
      });
      const legendH =
        legendInnerPadY +
        linesCount * shLegendLineH +
        max0(items.length - 1) * shLegendGap +
        legendInnerPadY;
      const totalH =
        PAD_TOP +
        shCardTitleH +
        GAP_LABEL_CONTENT +
        shCardOut * 2 +
        spaceBelowChart +
        legendH +
        PAD_BOTTOM;
      return Math.min(totalH, 800);
    };
    const max0 = (n: number) => (n > 0 ? n : 0);
    const heights = cards.map((c) => measureCardH(c));
    const shRowH = Math.max(...heights);

    y = ensureSpace(doc, y, shRowH, M, headerHeight);

    cards.forEach((card, idx) => {
      const x = M + idx * (shW + shRowGapX);
      const items = card.items;
      const sum = items.reduce((s, d) => s + d.valueNum, 0) || 1;

      doc.setLineWidth(0.6);
      doc.setDrawColor(225);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, y, shW, shRowH, 10, 10, "FD");

      try {
        doc.setFont("helvetica", LABEL_WEIGHT);
      } catch {
        doc.setFont(undefined as any, LABEL_WEIGHT);
      }
      doc.setFontSize(LABEL_SIZE);
      doc.setTextColor(40);
      doc.text(card.title, x + PAD_L, y + PAD_TOP);

      const dcx = x + shW / 2;
      const dcy =
        y + PAD_TOP + shCardTitleH + GAP_LABEL_CONTENT + shCardOut + 2;
      let a0 = -Math.PI / 2;
      items.forEach((d, i) => {
        const frac = d.valueNum / sum;
        const a1 = a0 + frac * Math.PI * 2;
        const base = card.parentBase
          ? card.parentBase
          : i % 2 === 0
          ? baseA
          : baseB;
        const lighten =
          items.length <= 1 ? 0 : (i / Math.max(1, items.length - 1)) * 0.5;
        const colr = mix(base, lighten);
        drawWedge(doc, dcx, dcy, shCardOut, a0, a1, colr);
        a0 = a1;
      });
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.1);
      doc.circle(dcx, dcy, shCardIn, "F");

      // Legend container
      const legendX = x + PAD_L;
      const legendY = dcy + shCardOut + spaceBelowChart;
      const legendW = shW - PAD_L - PAD_R;
      const maxLegendH = y + shRowH - PAD_BOTTOM - legendY;
      doc.setLineWidth(0.5);
      doc.setDrawColor(230);
      doc.setFillColor(250, 250, 252);
      doc.roundedRect(
        legendX,
        legendY,
        legendW,
        maxLegendH,
        legendCorner,
        legendCorner,
        "FD"
      );

      doc.setFontSize(shLegendFont);
      let yCursor = legendY + legendInnerPadY + 4;
      const textW = legendW - legendInnerPadX * 2;
      for (let i = 0; i < items.length; i++) {
        const d = items[i];
        const pct = `${((d.valueNum / sum) * 100).toFixed(1)}%`;
        const lbl = `${toTitleCase(d.tag)} ${pct}`;
        const wrapped = doc.splitTextToSize(lbl, textW - 14);
        const lines = Array.isArray(wrapped) ? wrapped.length : 1;
        const needed = lines * shLegendLineH + shLegendGap;
        if (yCursor + needed + legendInnerPadY > legendY + maxLegendH) {
          const remaining = items.length - i;
          doc.setTextColor(90);
          doc.setFontSize(8);
          doc.text(
            `+${remaining} more`,
            legendX + legendW - 44,
            legendY + maxLegendH - 8
          );
          break;
        }
        // bullet
        const base = card.parentBase
          ? card.parentBase
          : i % 2 === 0
          ? baseA
          : baseB;
        const lighten =
          items.length <= 1 ? 0 : (i / Math.max(1, items.length - 1)) * 0.5;
        const colr = mix(base, lighten);
        doc.setFillColor(colr.r, colr.g, colr.b);
        doc.setDrawColor(colr.r, colr.g, colr.b);
        doc.circle(legendX + legendInnerPadX + 4, yCursor - 3, 2.5, "FD");
        doc.setTextColor(42);
        doc.text(wrapped as any, legendX + legendInnerPadX + 10, yCursor);
        yCursor += needed;
      }
    });

    y += shRowH + 12;
  }

  // ===== 4) Optional Liquidity Indicator =====
  if (opts.liquidityIndicator) {
    ({ nextY: y } = generateLiquidityIndicatorSection(doc, opts.liquidityIndicator, {
      startY: y + 12,
      margin: M,
      headerHeight,
    }));
    y += 12;
  }

  // ===== 5) Debenture Trustee & Registrar Details =====
  {
    const twoColGapX = 12;
    const colW = fullW - twoColGapX;
    const leftW = colW / 2;
    const rightW = colW / 2;

    const dtName = sanitize(
      bond?.debentureTrusteeDetails?.nameOfTrustee || "-"
    );
    const dtAddr = sanitize(bond?.debentureTrusteeDetails?.address || "-");
    const dtPhone = sanitize(bond?.debentureTrusteeDetails?.phone || "-");

    const rgName = sanitize(bond?.registrarDetails?.nameOfRegistrar || "-");
    const rgAddr = sanitize(bond?.registrarDetails?.address || "-");
    const rgPhone = sanitize(bond?.registrarDetails?.phone || "-");

    const labelH = 14;
    const gapBetweenHeadAndAddr = 8;
    const gapBetweenAddrAndPhone = 12;

    const normalSize = TEXT_SIZE;
    const smallSize = 9.0;
    const lineHNormal = normalSize * 1.35;
    const lineHSmall = smallSize * 1.5;

    trySetFont(doc, "PlusJakartaSans", "bold", {
      name: "helvetica",
      style: "bold",
    });
    doc.setFontSize(normalSize);
    const dtNameLines = doc.splitTextToSize(dtName, leftW - PAD_L - PAD_R);
    const dtNameH =
      (Array.isArray(dtNameLines) ? dtNameLines.length : 1) * lineHNormal;
    try {
      doc.setFont("helvetica", "italic");
    } catch {
      doc.setFont("helvetica", "normal");
    }
    doc.setFontSize(smallSize);
    const dtAddrLines = doc.splitTextToSize(dtAddr, leftW - PAD_L - PAD_R);
    const dtAddrH =
      (Array.isArray(dtAddrLines) ? dtAddrLines.length : 1) * lineHSmall;
    try {
      doc.setFont("helvetica", "italic");
    } catch {
      doc.setFont("helvetica", "normal");
    }
    doc.setFontSize(smallSize);
    const dtPhoneLines = doc.splitTextToSize(dtPhone, leftW - PAD_L - PAD_R);
    const dtPhoneH =
      (Array.isArray(dtPhoneLines) ? dtPhoneLines.length : 1) * lineHSmall;
    const leftContentH = dtNameH + 6 + dtAddrH + 4 + dtPhoneH;

    trySetFont(doc, "PlusJakartaSans", "bold", {
      name: "helvetica",
      style: "bold",
    });
    doc.setFontSize(normalSize);
    const rgNameLines = doc.splitTextToSize(rgName, rightW - PAD_L - PAD_R);
    const rgNameH =
      (Array.isArray(rgNameLines) ? rgNameLines.length : 1) * lineHNormal;
    try {
      doc.setFont("helvetica", "italic");
    } catch {
      doc.setFont("helvetica", "normal");
    }
    doc.setFontSize(smallSize);
    const rgAddrLines = doc.splitTextToSize(rgAddr, rightW - PAD_L - PAD_R);
    const rgAddrH =
      (Array.isArray(rgAddrLines) ? rgAddrLines.length : 1) * lineHSmall;
    try {
      doc.setFont("helvetica", "italic");
    } catch {
      doc.setFont("helvetica", "normal");
    }
    doc.setFontSize(smallSize);
    const rgPhoneLines = doc.splitTextToSize(rgPhone, rightW - PAD_L - PAD_R);
    const rgPhoneH =
      (Array.isArray(rgPhoneLines) ? rgPhoneLines.length : 1) * lineHSmall;
    const rightContentH = rgNameH + 6 + rgAddrH + 4 + rgPhoneH;

    const rowH = Math.max(
      PAD_TOP + labelH + GAP_LABEL_CONTENT + leftContentH + PAD_BOTTOM,
      PAD_TOP + labelH + GAP_LABEL_CONTENT + rightContentH + PAD_BOTTOM
    );

    y = ensureSpace(doc, y, rowH, M, headerHeight);

    // Left card
    const leftX = M;
    doc.setLineWidth(0.6);
    doc.setDrawColor(CARD_BORDER);
    doc.roundedRect(leftX, y, leftW, rowH, 10, 10, "S");
    try {
      doc.setFont("helvetica", LABEL_WEIGHT);
    } catch {
      doc.setFont(undefined as any, LABEL_WEIGHT);
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text("Debenture Trustee Details", leftX + PAD_L, y + PAD_TOP);

    let ly = y + PAD_TOP + labelH + GAP_LABEL_CONTENT;
    try {
      doc.setFont("helvetica", "bold");
    } catch {
      doc.setFont(undefined as any, LABEL_WEIGHT);
    }
    doc.setFontSize(normalSize);
    doc.setTextColor(32);
    doc.text(dtNameLines as any, leftX + PAD_L, ly, {
      maxWidth: leftW - PAD_L - PAD_R,
    });
    ly += dtNameH + gapBetweenHeadAndAddr;
    try {
      doc.setFont("helvetica", "italic");
    } catch {
      doc.setFont("helvetica", "normal");
    }
    doc.setFontSize(smallSize);
    doc.setTextColor(60);
    doc.text(dtAddrLines as any, leftX + PAD_L, ly, {
      maxWidth: leftW - PAD_L - PAD_R,
    });
    ly += dtAddrH + gapBetweenAddrAndPhone;
    try {
      doc.setFont("helvetica", "italic");
    } catch {
      doc.setFont("helvetica", "normal");
    }
    doc.setFontSize(smallSize);
    doc.setTextColor(60);
    doc.text(dtPhoneLines as any, leftX + PAD_L, ly, {
      maxWidth: leftW - PAD_L - PAD_R,
    });

    // Right card
    const rightX = M + leftW + twoColGapX;
    doc.setLineWidth(0.6);
    doc.setDrawColor(CARD_BORDER);
    doc.roundedRect(rightX, y, rightW, rowH, 10, 10, "S");
    try {
      doc.setFont("helvetica", LABEL_WEIGHT);
    } catch {
      doc.setFont(undefined as any, LABEL_WEIGHT);
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text("Registrar Details", rightX + PAD_L, y + PAD_TOP);

    let ry = y + PAD_TOP + labelH + GAP_LABEL_CONTENT;
    try {
      doc.setFont("helvetica", "bold");
    } catch {
      doc.setFont(undefined as any, LABEL_WEIGHT);
    }
    doc.setFontSize(normalSize);
    doc.setTextColor(32);
    doc.text(rgNameLines as any, rightX + PAD_L, ry, {
      maxWidth: rightW - PAD_L - PAD_R,
    });
    ry += rgNameH + gapBetweenHeadAndAddr;
    try {
      doc.setFont("helvetica", "italic");
    } catch {
      doc.setFont("helvetica", "normal");
    }
    doc.setFontSize(smallSize);
    doc.setTextColor(60);
    doc.text(rgAddrLines as any, rightX + PAD_L, ry, {
      maxWidth: rightW - PAD_L - PAD_R,
    });
    ry += rgAddrH + gapBetweenAddrAndPhone;
    try {
      doc.setFont("helvetica", "italic");
    } catch {
      doc.setFont("helvetica", "normal");
    }
    doc.setFontSize(smallSize);
    doc.setTextColor(60);
    doc.text(rgPhoneLines as any, rightX + PAD_L, ry, {
      maxWidth: rightW - PAD_L - PAD_R,
    });

    y += rowH + 12;
  }

  // ===== 6) Rating Drivers (Pros first, then Cons) =====
  {
    const rdTitle = report?.ratingDriversHeading ?? "Rating Drivers";
    const BODY_FONT = PlusJakartaSansFontBase64String
      ? "PlusJakartaSans"
      : "helvetica";

    // Spacing above section heading
    y += 24;
    y = ensureSpace(doc, y, LABEL_SIZE + 12, M, headerHeight);

    try {
      doc.setFont("helvetica", LABEL_WEIGHT);
    } catch {
      doc.setFont(undefined as any, LABEL_WEIGHT);
    }
    doc.setFontSize(LABEL_SIZE);
    doc.setTextColor(40);
    doc.text(rdTitle, M + PAD_L, y);
    y += 8;

    const prosArr = (report?.pros ?? []).map((p: any) => ({
      kind: "pro",
      heading: sanitize(p?.heading || "-"),
      text: normalizeRupees(p?.text || "-"),
    }));
    const consArr = (report?.cons ?? []).map((c: any) => ({
      kind: "con",
      heading: sanitize(c?.heading || "-"),
      text: normalizeRupees(c?.text || "-"),
    }));
    const rdItems = [...prosArr, ...consArr];

    // 4-column layout
    const cols = 3;
    const gapX = 12;
    const colW = (fullW - (cols - 1) * gapX) / cols;

    // Typography (smaller)
    const headSize = 9.2;
    const textSize = 8.4;
    const headLH = headSize * 1.35;
    const textLH = textSize * 1.45;

    const padT = PAD_TOP;
    const padB = PAD_BOTTOM;

    const measureCardH = (
      w: number,
      item: { heading: string; text: string }
    ) => {
      doc.setFontSize(headSize);
      const hLines = doc.splitTextToSize(item.heading, w - PAD_L - PAD_R);
      const hH = (Array.isArray(hLines) ? hLines.length : 1) * headLH;

      doc.setFontSize(textSize);
      const tLines = doc.splitTextToSize(item.text, w - PAD_L - PAD_R);
      const tH = (Array.isArray(tLines) ? tLines.length : 1) * textLH;

      return padT + hH + 4 + tH + padB;
    };

    let idxItem = 0;
    while (idxItem < rdItems.length) {
      const rowItems = [];
      for (let c = 0; c < cols; c++) {
        rowItems.push(rdItems[idxItem + c]);
      }

      const heights = rowItems.map((it) => (it ? measureCardH(colW, it) : 0));
      const rowH = Math.max(...heights, padT + 20 + padB);

      y = ensureSpace(doc, y, rowH, M, headerHeight);

      for (let c = 0; c < cols; c++) {
        const it = rowItems[c];
        if (!it) continue;

        const x = M + c * (colW + gapX);

        // Color by kind (no explicit "Pro/Con" label anymore)
        const fill =
          it.kind === "pro"
            ? { r: 247, g: 253, b: 249 }
            : { r: 255, g: 247, b: 247 };
        const bord =
          it.kind === "pro"
            ? { r: 220, g: 242, b: 228 }
            : { r: 253, g: 222, b: 222 };
        doc.setLineWidth(0.6);
        doc.setDrawColor(bord.r, bord.g, bord.b);
        doc.setFillColor(fill.r, fill.g, fill.b);
        doc.roundedRect(x, y, colW, rowH, 10, 10, "FD");

        // Heading (bold)
        try {
          doc.setFont(BODY_FONT, "bold");
        } catch {
          doc.setFont("helvetica", "bold");
        }
        doc.setFontSize(headSize);
        // Heading color matches card type
        if (it.kind === "pro") {
          doc.setTextColor(46, 164, 79);
        } else {
          doc.setTextColor(220, 67, 67);
        }
        const hLines = doc.splitTextToSize(it.heading, colW - PAD_L - PAD_R);
        doc.text(hLines as any, x + PAD_L, y + padT, {
          maxWidth: colW - PAD_L - PAD_R,
        });
        const hH = (Array.isArray(hLines) ? hLines.length : 1) * headLH;
        // Divider below heading
        // Body (normal)
        try {
          doc.setFont(BODY_FONT, "normal");
        } catch {
          doc.setFont("helvetica", "normal");
        }
        doc.setFontSize(textSize);
        doc.setTextColor(50);
        const tLines = doc.splitTextToSize(it.text, colW - PAD_L - PAD_R);
        doc.text(tLines as any, x + PAD_L, y + padT + hH + 4, {
          maxWidth: colW - PAD_L - PAD_R,
        });
      }

      y += rowH + 12;
      idxItem += cols;
    }
  }

  // ===== 7) Recent New (only if news available) =====
  {
    const items = (news ?? []).slice(0, 3);
    if (items.length) {
      const cols = 3;
      const gapX = 12;
      const fullW = doc.internal.pageSize.getWidth() - 2 * M;
      const colW = (fullW - (cols - 1) * gapX) / cols;

      const dateSize = 9.0,
        titleSize = 10.2,
        bodySize = 9.2;
      const dateLH = dateSize * 1.35,
        titleLH = titleSize * 1.35,
        bodyLH = bodySize * 1.45;

      const heights = items.map((it) => {
        // date (single line, italic)
        const dH = dateLH;
        // title
        trySetFont(doc, "PlusJakartaSans", "bold", {
          name: "helvetica",
          style: "bold",
        });
        doc.setFontSize(titleSize);
        const newsTitle = normalizeRupees(it?.title || "-");
        const tLines = doc.splitTextToSize(newsTitle, colW - PAD_L - PAD_R);
        const tH = (Array.isArray(tLines) ? tLines.length : 1) * titleLH;
        // body
        trySetFont(doc, "PlusJakartaSans", "normal", {
          name: "helvetica",
          style: "normal",
        });
        doc.setFontSize(bodySize);
        const newsBody = normalizeRupees(it?.body || "-");
        const bLines = doc.splitTextToSize(newsBody, colW - PAD_L - PAD_R);
        const bH = (Array.isArray(bLines) ? bLines.length : 1) * bodyLH;

        return PAD_TOP + dH + 4 + tH + 4 + bH + PAD_BOTTOM;
      });

      const rowH = Math.max(...heights, PAD_TOP + 40 + PAD_BOTTOM);
      const headingH = LABEL_SIZE + 10;

      // Ensure space for heading + its row so they stick together
      y += 12;
      y = ensureSpace(doc, y, headingH + rowH, M, headerHeight);

      // Heading
      try {
        doc.setFont("helvetica", LABEL_WEIGHT as any);
      } catch {
        doc.setFont("helvetica", "bold");
      }
      doc.setFontSize(LABEL_SIZE);
      doc.setTextColor(40);
      doc.text("Recent News", M + PAD_L, y);
      y += 8;

      // Cards
      for (let i = 0; i < cols; i++) {
        const it = items[i];
        if (!it) continue;
        const x = M + i * (colW + gapX);

        // Page-1 style card colors
        doc.setLineWidth(0.6);
        doc.setDrawColor(225);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(x, y, colW, rowH, 10, 10, "FD");

        // Date: grey, italic
        try {
          doc.setFont("helvetica", "italic");
        } catch {
          doc.setFont("helvetica", "normal");
        }
        doc.setFontSize(dateSize);
        doc.setTextColor(90);
        const dateStr = moment(it?.publishedAt, "YYYY-MM-DD").format(
          "MMM DD, YYYY"
        );
        doc.text(dateStr, x + PAD_L, y + PAD_TOP);

        // Title: bold
        trySetFont(doc, "PlusJakartaSans", "bold", {
          name: "helvetica",
          style: "bold",
        });
        doc.setFontSize(titleSize);
        doc.setTextColor(32);
        const newsTitle = normalizeRupees(it?.title || "-");
        const tLines = doc.splitTextToSize(newsTitle, colW - PAD_L - PAD_R);
        doc.text(tLines as any, x + PAD_L, y + PAD_TOP + dateLH + 4, {
          maxWidth: colW - PAD_L - PAD_R,
        });

        // Body
        trySetFont(doc, "PlusJakartaSans", "normal", {
          name: "helvetica",
          style: "normal",
        });
        doc.setFontSize(bodySize);
        doc.setTextColor(50);
        const newsBody = normalizeRupees(it?.body || "-");
        const bLines = doc.splitTextToSize(newsBody, colW - PAD_L - PAD_R);
        const tH = (Array.isArray(tLines) ? tLines.length : 1) * titleLH;
        doc.text(bLines as any, x + PAD_L, y + PAD_TOP + dateLH + 4 + tH + 4, {
          maxWidth: colW - PAD_L - PAD_R,
        });
      }

      y += rowH + 12;
    }
  }

  // ===== 8) Final disclaimer =====
  {
    const fullW = doc.internal.pageSize.getWidth() - 2 * M;

    const disc1 =
      "Disclaimer: Investments in Debt Securities/ Municipal Debt Securities/ Securitised Debt Instruments are subject to risks including delay and/ or default in payment. Read all the Offer Related documents carefully.";
    const disc2 =
      "Source: Issuer website, Rating Rationale, Issuer Annual Report, Information Memorandum and News from various Media Outlets.";

    const discSize = 9.2;
    const discLH = discSize * 1.45;
    const gapBetween = 8; // extra space between Disclaimer and Source

    trySetFont(doc, "PlusJakartaSans", "normal", {
      name: "helvetica",
      style: "normal",
    });
    doc.setFontSize(discSize);

    const lines1 = doc.splitTextToSize(disc1, fullW - PAD_L - PAD_R);
    const h1 = (Array.isArray(lines1) ? lines1.length : 1) * discLH;

    const lines2 = doc.splitTextToSize(disc2, fullW - PAD_L - PAD_R);
    const h2 = (Array.isArray(lines2) ? lines2.length : 1) * discLH;

    const h = PAD_TOP + h1 + gapBetween + h2 + PAD_BOTTOM;

    y = ensureSpace(doc, y, h, M, headerHeight);

    // Light red card
    doc.setLineWidth(0.6);
    doc.setDrawColor(248, 202, 202);
    doc.setFillColor(255, 239, 239);
    doc.roundedRect(M, y, fullW, h, 8, 8, "FD");

    doc.setTextColor(180, 40, 40);
    // First paragraph
    doc.text(lines1 as any, M + PAD_L, y + PAD_TOP, {
      maxWidth: fullW - PAD_L - PAD_R,
    });
    // Second paragraph with explicit gap
    doc.text(lines2 as any, M + PAD_L, y + PAD_TOP + h1 + gapBetween, {
      maxWidth: fullW - PAD_L - PAD_R,
    });

    y += h + 8;
  }
  const nextY = y;
  (doc as any).__nextY = nextY;
  return { doc, nextY };
}
